/**
 * Machine efficiency. What each stage made per working day, against what it
 * could make: the tape plant and each RP machine against the capacity set
 * for them in Settings, the loom floor against the capacity of the looms
 * that ran, and dispatch against the bags the plant made.
 *
 * The averages divide by working days — days with at least one shift entry
 * anywhere in the plant — so a Sunday the plant stood still does not count
 * against a machine, and a day it alone stood still does.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { controls, type EfficiencyReport, type EfficiencyRow } from "../../lib/controls";
import { fmtPct, fmtQty } from "../../lib/format";
import { comparisonLabel, describePeriod } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";
import { PeriodPicker } from "../../components/PeriodPicker";
import { useDrill } from "../../components/Drill";
import { Info } from "../../components/Info";
import { Cards } from "./ProductionReport";

/** Change in a figure against the comparison period, arrow and colour. */
function Change({ now, before, points = false }: {
  now: string | null;
  before: string | null;
  /** Percentages change in points, not in percent of themselves. */
  points?: boolean;
}) {
  if (now === null || before === null) return <span className="cell__unit">—</span>;
  const a = Number(now);
  const b = Number(before);
  if (!points && !b) return <span className="cell__unit">—</span>;
  const change = points ? a - b : ((a - b) / Math.abs(b)) * 100;
  const tone = Math.abs(change) < 0.05 ? "neutral" : change > 0 ? "positive" : "negative";
  const arrow = tone === "neutral" ? "→" : change > 0 ? "↑" : "↓";
  return (
    <span className={`kpi__delta kpi__delta--${tone}`}>
      {arrow} {points ? `${Math.abs(change).toFixed(1)} pts` : fmtPct(Math.abs(change).toFixed(1))}
    </span>
  );
}

export function MachineEfficiency() {
  const [period, setPeriod] = useReportPeriod();
  const [compare, setCompare] = useCompare();
  const [report, setReport] = useState<EfficiencyReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = useDrill();

  const load = useCallback(() => {
    setReport(null);
    setError(null);
    controls
      .efficiency(period.from, period.to, period.shift, compare)
      .then(setReport)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const vs = comparisonLabel(period, compare);
  const info = report?.info ?? {};
  const sections = report
    ? (["Tape plant", "RP", "Loom", "Dispatch"] as const)
        .map((s) => [s, report.rows.filter((r) => r.section === s)] as const)
        .filter(([, rows]) => rows.length)
    : [];
  const unrated = report?.rows.filter(
    (r) => r.basis === "Capacity" && r.capacity_per_day === null && r.key !== "rp:total"
  ) ?? [];

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Machine efficiency</h2>
          <p className="page__sub">
            {describePeriod(period)} · {vs}
            {report && ` · ${report.working_days} working day${report.working_days === 1 ? "" : "s"}`}
            <Info text={info.days} />
          </p>
        </div>
        <div className="page__controls">
          <PeriodPicker value={period} onChange={setPeriod} shifts
                        compare={compare} onCompare={setCompare} />
          <ReportDownload title="Machine efficiency" period={period} />
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}
      {!report && !error && (
        <div className="kpirow">
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton kpi" style={{ height: 108 }} />)}
        </div>
      )}

      {report && (
        <>
          <Cards cards={report.cards} vs={vs} />

          {unrated.length > 0 && (
            <div className="banner banner--warning">
              No capacity per day is set for {unrated.map((r) => r.machine).join(", ")}, so
              their efficiency cannot be worked out. A super admin sets it in Settings → Machines.
            </div>
          )}

          <section className="card">
            <h3 className="section-title">Production per working day</h3>
            <p className="section-note">Press a row to see what it was summed from.</p>
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th>Machine</th>
                    <th className="num">Produced</th>
                    <th className="num">Average / day<Info text={info.avg} /></th>
                    <th className="num">Against / day<Info text={info.capacity} /></th>
                    <th>Measured against</th>
                    <th className="num">Efficiency<Info text={info.efficiency} /></th>
                    <th className="num">Average change</th>
                    <th className="num">Efficiency change</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.flatMap(([section, rows]) =>
                    rows.map((r: EfficiencyRow, i) => (
                      <tr key={r.key} className="rowlink" tabIndex={0}
                          onClick={() => open(r.drill)}
                          onKeyDown={(e) => e.key === "Enter" && open(r.drill)}>
                        <td>{i === 0 ? section : ""}</td>
                        <td>
                          {r.key.endsWith(":total") ? <strong>{r.machine}</strong> : r.machine}
                          <Info text={r.info} />
                        </td>
                        <td className="num mono">{fmtQty(r.produced, 0)} <span className="cell__unit">{r.unit}</span></td>
                        <td className="num mono">{r.avg_per_day === null ? "—" : fmtQty(r.avg_per_day, 0)}</td>
                        <td className="num mono">{r.capacity_per_day === null ? "—" : fmtQty(r.capacity_per_day, 0)}</td>
                        <td><span className="cell__unit">{r.basis}</span></td>
                        <td className="num mono">{fmtPct(r.efficiency_pct)}</td>
                        <td className="num"><Change now={r.avg_per_day} before={r.previous_avg_per_day} /></td>
                        <td className="num"><Change now={r.efficiency_pct} before={r.previous_efficiency_pct} points /></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
